// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import {
  displayNetStrokes,
  stablefordPoints,
  strokesReceivedClampedAtScratch,
} from '../../utils/scoring';
import { translateEveryLocale } from '@/i18n';
import { BunkerIcon, ChipInIcon, OutIcon, PenaltyIcon, ThreePuttIcon, WaterIcon } from './assets';
import type { HoleEntry, HoleEventEntry, NetResult, Player, ScoreTotal } from './types';

export const normalizePlayerKey = (key: string | undefined | null) => key?.trim().toLowerCase() ?? '';
export const isUuidLike = (value?: string) =>
  !!value && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value);

// A player's `name` doubles as an identity key throughout the hole screen, so it can never be
// blank — when a name is missing it falls back to the id (a GUID). `playerHasRealName` lets the
// UI tell a genuine name apart from that GUID fallback so it can show a placeholder instead.
export const playerHasRealName = (name?: string) => !!name && !isUuidLike(name);

// Merge a freshly seeded player list (e.g. from a websocket round/leaderboard event) onto the
// existing one. The incoming list defines the current participants, but real names, handicaps and
// avatars already known from a prior source are preserved so a name-less payload can't clobber them
// back to a GUID. This is what stops the "GUID flashes before the real name loads" regression.
export const mergeSeededPlayers = (prev: Player[], incoming: Player[]): Player[] => {
  const prevById = new Map(prev.map((p) => [p.id, p]));
  return incoming.map((p) => {
    const existing = prevById.get(p.id);
    const incomingName = playerHasRealName(p.name) ? p.name : '';
    const existingName = playerHasRealName(existing?.name) ? (existing as Player).name : '';
    return {
      id: p.id,
      name: incomingName || existingName || p.id,
      createdAt: p.createdAt || existing?.createdAt || '',
      handicap: existing?.handicap ?? p.handicap,
      imageKey: p.imageKey ?? existing?.imageKey,
      imageThumbKey: p.imageThumbKey ?? existing?.imageThumbKey,
    };
  });
};

/**
 * The "Ghost of " / "Spöket " / "Spøgelset " a ghost player's name is built with, in every locale
 * rather than just the current one.
 *
 * The prefix is baked into the name when the ghost is created, so a round created in Swedish keeps
 * "Spöket Anders" for a player reading the app in English. Matching only the current locale's
 * prefix left those ghosts unrecognised — no icon, and the name shortened to "Spöket A.".
 *
 * Longest first, so a prefix that starts with another one still wins.
 */
const resolveGhostPrefixes = (): string[] => {
  const marker = '\u0000';
  return translateEveryLocale('ai.ghostNamePrefix', { name: marker })
    .filter((template) => template.includes(marker))
    .map((template) => template.split(marker)[0]?.trim() ?? '')
    .filter((prefix) => prefix.length > 0)
    .sort((a, b) => b.length - a.length);
};

const matchGhostPrefix = (name: string): string | null =>
  resolveGhostPrefixes().find((prefix) => name.toLowerCase().startsWith(`${prefix.toLowerCase()} `)) ?? null;

/**
 * Whether a name was generated as a ghost of a real player. Used where the round's own `isAi`
 * flag is not available (the leaderboard works off score totals, not player records) and as a
 * fallback where it is but was never populated.
 */
export const isGhostPlayerName = (name?: string | null): boolean => {
  const trimmed = (name ?? '').trim();
  return trimmed.length > 0 && matchGhostPrefix(trimmed) !== null;
};

const compactRealName = (parts: string[], fallback: string) => {
  if (parts.length <= 1) return parts[0] ?? fallback;
  const initial = parts[1]?.[0] ?? '';
  return initial ? `${parts[0]} ${initial}.` : parts[0];
};

/**
 * A player's name shortened for a narrow column: "Ulf Mansson" reads as "Ulf M.".
 *
 * Ghosts are prefixed with the same words in every name ("Ghost of Agnes", "Ghost of Ewa"), so
 * shortening them the same way collapses a whole round into one repeated label — every column
 * headed "Ghost o.", identifying nobody. Their shared prefix is abbreviated instead, leaving the
 * part that actually names them: "Gh. Agnes".
 */
export const compactPlayerName = (name: string) => {
  const trimmed = name.trim();
  const ghostPrefix = matchGhostPrefix(trimmed);

  if (ghostPrefix) {
    const inner = trimmed.slice(ghostPrefix.length).trim();
    const innerParts = inner.split(/\s+/).filter(Boolean);
    if (innerParts.length > 0) {
      const badge = ghostPrefix.split(/\s+/)[0]?.slice(0, 2) ?? '';
      const compactedInner = compactRealName(innerParts, inner);
      return badge ? `${badge}. ${compactedInner}` : compactedInner;
    }
  }

  return compactRealName(trimmed.split(/\s+/).filter(Boolean), name);
};

export const hazardTypeFromName = (name?: string) => {
  const normalized = (name ?? '').toLowerCase();
  // Whole-word, before the includes() chain: 'out' is short enough to appear inside an
  // unrelated game name (a knockout, a shootout) and claim it as a hazard.
  if (normalized.trim() === 'out') return 'out';
  if (normalized.includes('water')) return 'water';
  if (normalized.includes('penalty')) return 'penalty';
  if (normalized.includes('bunker')) return 'bunker';
  if (normalized.includes('chip') && normalized.includes('in')) return 'chipin';
  if (
    normalized.includes('3 putts') ||
    normalized.includes('three putts') ||
    normalized.includes('3putt') ||
    normalized.includes('threeputt')
  ) {
    return 'threeputt';
  }
  return null;
};

export const hazardIconForGameName = (name?: string) => {
  const normalized = (name ?? '').toLowerCase();
  if (normalized.trim() === 'out') return OutIcon;
  if (normalized.includes('water')) return WaterIcon;
  if (normalized.includes('penalty')) return PenaltyIcon;
  if (normalized.includes('bunker')) return BunkerIcon;
  if (normalized.includes('chip') && normalized.includes('in')) return ChipInIcon;
  if (
    normalized.includes('3 putts') ||
    normalized.includes('three putts') ||
    normalized.includes('3putt') ||
    normalized.includes('threeputt')
  ) {
    return ThreePuttIcon;
  }
  return null;
};

export const parseHandicapValue = (raw: unknown): number | undefined => {
  if (typeof raw === 'number' && Number.isFinite(raw)) return raw;
  if (typeof raw === 'string') {
    const parsed = parseFloat(raw);
    if (Number.isFinite(parsed)) return parsed;
  }
  return undefined;
};

export const computeNetForHole = ({
  gross,
  handicap,
  holeIndex,
  strokeIndex,
  par,
}: {
  gross?: string | number | null;
  handicap?: number | null;
  holeIndex?: number | null;
  strokeIndex?: number | null;
  par?: number | null;
}): NetResult | null => {
  if (gross === null || gross === undefined) return null;
  const grossStrokes = typeof gross === 'string' ? parseInt(gross, 10) : gross;
  if (!Number.isFinite(grossStrokes) || grossStrokes <= 0) return null;

  const normalizedHandicap = handicap !== null && handicap !== undefined ? handicap : null;
  const normalizedHoleIndex = strokeIndex ?? holeIndex ?? null;
  const normalizedPar = par !== null && par !== undefined ? par : null;

  const strokesReceived =
    normalizedHandicap !== null ? strokesReceivedClampedAtScratch(normalizedHandicap, normalizedHoleIndex) : 0;

  const netStrokes = displayNetStrokes(grossStrokes, false, strokesReceived) ?? grossStrokes;
  const netVsPar = normalizedPar !== null ? netStrokes - normalizedPar : undefined;
  const netVsParLabel =
    netVsPar === undefined
      ? undefined
      : netVsPar === 0
        ? 'E'
        : netVsPar > 0
          ? `+${netVsPar}`
          : `${netVsPar}`;

  return { netStrokes, strokesReceived, netVsPar, netVsParLabel };
};

export const normalizeGameSlug = (name?: string | null) => {
  if (!name) return '';
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
};

export const normalizeHoleEntry = (entry: Record<string, unknown>): HoleEntry => {
  const playerId =
    (entry as { playerId?: string }).playerId ??
    (entry as { player_id?: string }).player_id ??
    (entry as { PlayerId?: string }).PlayerId ??
    (entry as { player?: string }).player ??
    null;
  const playerName =
    (entry as { playerName?: string }).playerName ??
    (entry as { player?: string }).player ??
    (entry as { Player?: string }).Player ??
    '';
  const rawPickup =
    (entry as { pickup?: unknown }).pickup ??
    (entry as { Pickup?: unknown }).Pickup;
  const pickup =
    typeof rawPickup === 'boolean'
      ? rawPickup
      : typeof rawPickup === 'string'
        ? rawPickup.toLowerCase() === 'true'
        : false;
  return {
    id: (entry as { id?: string }).id ?? '',
    roundId: (entry as { roundId?: string }).roundId ?? (entry as { RoundId?: string }).RoundId ?? '',
    playerId: playerId ?? undefined,
    holeNumber:
      (entry as { holeNumber?: number }).holeNumber ??
      (entry as { HoleNumber?: number }).HoleNumber ??
      (entry as { hole?: number }).hole ??
      0,
    strokeIndex:
      (entry as { strokeIndex?: number }).strokeIndex ??
      (entry as { StrokeIndex?: number }).StrokeIndex ??
      (entry as { holeIndex?: number }).holeIndex ??
      (entry as { HoleIndex?: number }).HoleIndex ??
      (entry as { index?: number }).index,
    playerName,
    grossStrokes:
      (entry as { grossStrokes?: number | null }).grossStrokes ??
      (entry as { GrossStrokes?: number | null }).GrossStrokes ??
      (entry as { grossStroke?: number | null }).grossStroke ??
      (entry as { GrossStroke?: number | null }).GrossStroke ??
      (entry as { strokes?: number | null }).strokes ??
      (entry as { Strokes?: number | null }).Strokes ??
      (entry as { value?: number | null }).value ??
      null,
    pickup,
    playingHandicap:
      (entry as { playingHandicap?: number }).playingHandicap ??
      (entry as { playing_handicap?: number }).playing_handicap ??
      (entry as { Handicap?: number }).Handicap ??
      (entry as { handicap?: number }).handicap ??
      null,
    createdAt:
      (entry as { updatedAt?: string }).updatedAt ??
      (entry as { UpdatedAt?: string }).UpdatedAt ??
      (entry as { createdAt?: string }).createdAt ??
      (entry as { CreatedAt?: string }).CreatedAt ??
      '',
    handicapStrokes:
      (entry as { handicapStrokes?: number }).handicapStrokes ??
      (entry as { HandicapStrokes?: number }).HandicapStrokes ??
      (entry as { strokesReceived?: number }).strokesReceived ??
      (entry as { StrokesReceived?: number }).StrokesReceived ??
      (entry as { strokes_received?: number }).strokes_received,
    netStrokes:
      (entry as { netStrokes?: number | null }).netStrokes ??
      (entry as { NetStrokes?: number | null }).NetStrokes ??
      null,
    // Left undefined rather than null when absent: "the server has not answered yet" falls back
    // to the local derivation, while an explicit null means the hole cannot answer it.
    greenInRegulation:
      (entry as { greenInRegulation?: boolean | null }).greenInRegulation ??
      (entry as { GreenInRegulation?: boolean | null }).GreenInRegulation,
  };
};

export const computeStrokesReceivedForHole = (handicap: number | null | undefined, holeIndex: number | null | undefined) => {
  if (handicap === null || handicap === undefined) {
    return 0;
  }
  return strokesReceivedClampedAtScratch(handicap, holeIndex);
};

// Stableford preview for the score currently being entered (no server value can exist yet).
// Uses the UNCLAMPED net via the shared formula: a hole-in-one with a stroke received on a
// par 3 is 5 points — deriving from computeNetForHole's clamped net wrongly showed 4.
export const computeStablefordPointsForHole = ({
  gross,
  handicap,
  strokeIndex,
  par,
}: {
  gross?: string | number | null;
  handicap?: number | null;
  strokeIndex?: number | null;
  par: number;
}): number | null => {
  if (gross === null || gross === undefined) return null;
  const grossStrokes = typeof gross === 'string' ? parseInt(gross, 10) : gross;
  if (!Number.isFinite(grossStrokes) || grossStrokes <= 0) return null;
  return stablefordPoints(par, strokeIndex, handicap ?? 0, grossStrokes, false);
};

export const normalizeTotalsList = (totals: ScoreTotal[], players: Array<{ id: string; name: string }>): ScoreTotal[] =>
  totals.map((t) => {
    const match = players.find((p) => p.id === t.playerId || p.name === t.playerName);
    const resolvedId = t.playerId ?? match?.id ?? t.playerName ?? '';
    const resolvedName = match?.name ?? t.playerName ?? t.playerId ?? 'Okänd spelare';
    return {
      ...t,
      playerId: resolvedId,
      playerName: resolvedName,
      totalStrokes: t.totalStrokes ?? 0,
      totalRoundGamePoints: t.totalRoundGamePoints ?? (t as { totalPoints?: number }).totalPoints ?? undefined,
      tourGamesPoints: t.tourGamesPoints ?? undefined,
      pickups: t.pickups ?? 0,
    };
  });

export const normalizeHoleEvent = (entry: Record<string, unknown>): HoleEventEntry | null => {
  const playerId =
    (entry as { playerId?: string }).playerId ??
    (entry as { PlayerId?: string }).PlayerId ??
    '';
  const holeNumber =
    (entry as { holeNumber?: number }).holeNumber ??
    (entry as { HoleNumber?: number }).HoleNumber ??
    0;
  const eventType =
    (entry as { eventType?: string }).eventType ??
    (entry as { EventType?: string }).EventType ??
    '';
  if (!playerId || !eventType || !holeNumber) return null;
  const eventValue =
    (entry as { eventValue?: string }).eventValue ??
    (entry as { EventValue?: string }).EventValue ??
    null;
  const occurredAt =
    (entry as { occurredAt?: string }).occurredAt ??
    (entry as { OccurredAt?: string }).OccurredAt ??
    null;
  return { playerId, holeNumber, eventType, eventValue, occurredAt };
};
